import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useGetAllChats from '@/hooks/useGetAllChats';
import useTenants from '@/hooks/useTenants';
import Card from '@/components/shared/Card';
import UIPageTitle from '@/components/shared/UIPageTitle';
import UIButton from '@/components/shared/UIButton';
import UIIconButton from '@/components/shared/UIIconButton';
import UITwoColumnWrapper from '@/components/shared/UITwoColumnWrapper';
import ActiveConversationsList from '@/components/shared/Chat/ActiveConversationsList';
import ConversationView from '@/components/shared/Chat/ConversationView';
import NewConversationModal from '@/components/shared/Chat/NewConversationModal';
import type { ConversationData } from '@/components/shared/Chat/ActiveConversationsList';
import type { TenantData } from '@/components/shared/Chat/UITenantTable';
import './ChatsPage.scss';
import { useSelector } from 'react-redux';
import { profileSelector } from '@/store/profile/profileSlices';
import { safeToLowerCase } from '@/utils/stringUtils';

const baseClass = 'ChatsPage';

const EmptyChatPlaceholder = () => (
  <Card className={`${baseClass}__placeholder-card`}>
    <Card.Body className={`${baseClass}__placeholder-body`}>
      <div className={`${baseClass}__placeholder-content`}>
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="#C4CDD5"/>
          <path d="M7 9H17V11H7V9Z" fill="#C4CDD5"/>
          <path d="M7 12H14V14H7V12Z" fill="#C4CDD5"/>
        </svg>
        <h4 className={`${baseClass}__placeholder-title`}>Select a conversation</h4>
        <p>Choose from your existing conversations to start chatting.</p>
      </div>
    </Card.Body>
  </Card>
);

const ChatsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedChatId, setSelectedChatId] = useState<number | null>(() => {
    const urlChatId = searchParams.get('chatId');
    return urlChatId ? Number(urlChatId) : null;
  });
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('query') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const profile = useSelector(profileSelector);

  const { data: chatsData } = useGetAllChats();
  const { data: tenantsData, isLoading: tenantsLoading } = useTenants();

  const chats = useMemo(() => chatsData?.data || [], [chatsData?.data]);
  const tenants = useMemo(() => {
    if (!tenantsData) return [];
    return Array.isArray(tenantsData) ? tenantsData : [];
  }, [tenantsData]);

  const conversationsData: ConversationData[] = useMemo(() => {
    return chats.map((chat) => {
      const participant = chat.participants.find(
        (p) => p.id !== profile?.id
      );
      return {
        id: participant?.id || 0,
        name: participant ? `${participant.firstname} ${participant.lastname}` : 'Unknown',
        email: participant?.email || 'No email',
        avatar: participant?.avatar || null,
        unreadCount: chat.unreadMessagesCount || 0,
      };
    }).filter(conv => conv.id !== 0);
  }, [chats, profile]);

  const activeConversation = useMemo(() => {
    if (!selectedChatId) return undefined;

    const existingConversation = conversationsData.find(c => c.id === selectedChatId);
    if (existingConversation) {
      return existingConversation;
    }

    const tenant = tenants.find((t: any) => t.id === selectedChatId);
    if (tenant) {
      return {
        id: tenant.id,
        name: `${tenant.firstname} ${tenant.lastname}`,
        email: tenant.email || 'No email',
        avatar: tenant.avatar || null,
        unreadCount: 0,
      };
    }

    return undefined;
  }, [selectedChatId, conversationsData, tenants]);

  const tenantsForModal: TenantData[] = useMemo(() => {
    if (!tenants || !Array.isArray(tenants)) return [];
    return tenants.map((tenant: any) => ({
      id: tenant.id,
      name: `${tenant.firstname} ${tenant.lastname}`,
      email: tenant.email || 'No email',
      avatar: tenant.avatar || undefined,
    }));
  }, [tenants]);

  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const modalItemsPerPage = 10;

  const filteredTenants = useMemo(() => {
    if (!modalSearchTerm) return tenantsForModal;
    return tenantsForModal.filter(tenant =>
      safeToLowerCase(tenant.name).includes(safeToLowerCase(modalSearchTerm)) ||
      safeToLowerCase(tenant.email).includes(safeToLowerCase(modalSearchTerm))
    );
  }, [tenantsForModal, modalSearchTerm]);

  const modalStartIndex = (modalCurrentPage - 1) * modalItemsPerPage;
  const paginatedTenants = filteredTenants.slice(modalStartIndex, modalStartIndex + modalItemsPerPage);

  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversationsData;
    return conversationsData.filter(conv =>
      safeToLowerCase(conv.name).includes(safeToLowerCase(searchTerm)) ||
      safeToLowerCase(conv.email).includes(safeToLowerCase(searchTerm))
    );
  }, [conversationsData, searchTerm]);

  const itemsPerPage = 10;
  const totalItems = filteredConversations.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedConversations = filteredConversations.slice(startIndex, startIndex + itemsPerPage);

  const handleConversationClick = (participantId: number) => {
    setSelectedChatId(participantId);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('chatId', String(participantId));
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleInfoClick = (tenantId: number) => {
    navigate(`/dashboard/tenants/${tenantId}`);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
    const newSearchParams = new URLSearchParams(searchParams);
    if (searchValue) {
      newSearchParams.set('query', searchValue);
    } else {
      newSearchParams.delete('query');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleAttachFile = () => console.log('Attach file');
  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleTenantSelect = (tenantId: number) => {
    setSelectedChatId(tenantId);
    setIsNewConversationOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('chatId', String(tenantId));
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleNewConversationSearch = (searchValue: string) => {
    setModalSearchTerm(searchValue);
    setModalCurrentPage(1);
  };

  const handleNewConversationPageChange = (page: number) => {
    setModalCurrentPage(page);
  };

  const headerActions = (
    <>
      <UIButton
        variant="primary"
        outline
        size="medium"
        onClick={() => navigate('/dashboard')}
      >
        Back to Dashboard
      </UIButton>
      <UIButton
        variant="success"
        size="medium"
        startIcon={<UIIconButton iconType="contact" variant="success" size="small" />}
        onClick={() => setIsNewConversationOpen(true)}
      >
        New conversation
      </UIButton>
    </>
  );

  return (
    <div className={baseClass}>
      <NewConversationModal
        isOpen={isNewConversationOpen}
        onClose={() => {
          setIsNewConversationOpen(false);
          setModalSearchTerm('');
          setModalCurrentPage(1);
        }}
        onTenantSelect={handleTenantSelect}
        onInfoClick={handleInfoClick}
        tenants={tenantsLoading ? [] : paginatedTenants}
        totalItems={filteredTenants.length}
        itemsPerPage={modalItemsPerPage}
        currentPage={modalCurrentPage}
        onPageChange={handleNewConversationPageChange}
        onSearch={handleNewConversationSearch}
      />

      <UIPageTitle
        title="Conversations"
        action={headerActions}
        size="medium"
        showDivider={true}
      />

      <UITwoColumnWrapper
        gap="large"
        spacing="tight"
        ratio="1:1"
        mobileLayout="stack"
      >
        <ActiveConversationsList
          conversations={paginatedConversations}
          onConversationClick={handleConversationClick}
          onInfoClick={handleInfoClick}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
        />

        {selectedChatId ? (
          <ConversationView
            chatId={selectedChatId}
            activeConversation={activeConversation}
            onAttachFile={handleAttachFile}
            onInfoClick={handleInfoClick}
          />
        ) : (
          <EmptyChatPlaceholder />
        )}
      </UITwoColumnWrapper>
    </div>
  );
};

export default ChatsPage;
